"use client";

import { cn } from "@jesus-central-church/shared";
import { motion, useReducedMotion } from "motion/react";
import { Calendar, MapPin, Clock } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

interface TimeUnit {
  value: number;
  label: string;
}

interface NextServiceCardProperties {
  /** Title of the service (e.g., "Sunday Service") */
  title?: string;
  /** Target date/time for the countdown */
  serviceDate: Date;
  /** Location of the service */
  location?: string;
  /** Enable/disable animations */
  enableAnimations?: boolean;
  /** Custom content to render in the card footer */
  children?: ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Calculates the time remaining until a target date
 */
const getTimeRemaining = (targetDate: Date): number => {
  return Math.max(0, Math.floor((+targetDate - Date.now()) / 1000));
};

/**
 * Converts seconds into days, hours, minutes, and seconds
 */
const getTimeUnits = (totalSeconds: number): TimeUnit[] => {
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    { value: days, label: "Dagen" },
    { value: hours, label: "Uren" },
    { value: minutes, label: "Min" },
    { value: seconds, label: "Sec" },
  ];
};

/**
 * Formats a date for display in Dutch locale
 */
const formatServiceDate = (date: Date): string => {
  return date.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

/**
 * Formats a time for display in Dutch locale
 */
const formatServiceTime = (date: Date): string => {
  return date.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * NextServiceCard - A countdown component for upcoming church services
 *
 * Displays a countdown timer to the next service with service details.
 * Designed to integrate seamlessly into hero sections.
 */
export function NextServiceCard({
  title = "Volgende Dienst",
  serviceDate,
  location,
  enableAnimations = true,
  children,
  className,
}: NextServiceCardProperties) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeRemaining(serviceDate));
  const shouldReduceMotion = useReducedMotion();
  const shouldAnimate = enableAnimations && !shouldReduceMotion;

  useEffect(() => {
    const update = () => {
      setTimeLeft(getTimeRemaining(serviceDate));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [serviceDate]);

  const timeUnits = getTimeUnits(timeLeft);
  const isUrgent = timeLeft > 0 && timeLeft < 86_400; // Less than 24 hours
  const hasStarted = timeLeft === 0;

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05,
        duration: 0.8,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring" as const, stiffness: 400, damping: 28 },
    },
  };

  return (
    <motion.div
      data-slot="next-service-card"
      initial={shouldAnimate ? "hidden" : "visible"}
      animate="visible"
      variants={containerVariants}
      className={cn(
        "inline-flex flex-col items-end gap-3 text-freedom",
        className
      )}
    >
      {/* Header row with title and urgent badge */}
      <motion.div
        className="flex items-center gap-3"
        variants={shouldAnimate ? childVariants : undefined}
      >
        <div className="flex items-center gap-2 text-freedom/70">
          <Clock className="w-4 h-4" />
          <span className="text-sm font-medium tracking-wide">
            Next: {title}
          </span>
        </div>
        {isUrgent && (
          <span className="bg-herstel text-strategy-gold px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
            Soon!
          </span>
        )}
      </motion.div>

      {/* Countdown Display */}
      {hasStarted ? (
        <motion.div
          variants={shouldAnimate ? childVariants : undefined}
          className="flex items-center gap-2"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
          </span>
          <span className="text-sm font-bold text-green-400 uppercase tracking-wider">
            Live now!
          </span>
        </motion.div>
      ) : (
        <motion.div
          className="flex gap-1.5"
          variants={shouldAnimate ? childVariants : undefined}
        >
          {timeUnits.map((unit, index) => (
            <div
              key={unit.label}
              className={cn(
                "relative text-center min-w-[56px] py-2 px-2",
                "bg-boldness/40 backdrop-blur-sm rounded-lg",
                "border border-freedom/10",
                index === 3 && isUrgent && "border-herstel/50 bg-herstel/10"
              )}
            >
              <div
                className={cn(
                  "text-xl font-bold tabular-nums tracking-tight",
                  index === 3 && isUrgent ? "text-herstel" : "text-freedom"
                )}
              >
                {unit.value.toString().padStart(2, "0")}
              </div>
              <div className="text-[9px] text-freedom/50 font-semibold uppercase tracking-wider">
                {unit.label}
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Date & Location - subtle info line */}
      <motion.div
        className="flex items-center gap-4 text-xs text-freedom/50"
        variants={shouldAnimate ? childVariants : undefined}
      >
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3" />
          <span className="capitalize">{formatServiceDate(serviceDate)}</span>
          <span className="text-freedom/30">·</span>
          <span>{formatServiceTime(serviceDate)}</span>
        </div>
        {location && (
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            <span>{location}</span>
          </div>
        )}
      </motion.div>

      {/* Custom children content */}
      {children && (
        <motion.div variants={shouldAnimate ? childVariants : undefined}>
          {children}
        </motion.div>
      )}
    </motion.div>
  );
}
